<template>
  <div class="dashboard-sidebar">
    <!-- Logo Section -->
    <div class="dashboard-sidebar__header">
      <button class="dashboard-sidebar__logo-button" @click="handleLogoClick" aria-label="Go to dashboard">
      <div class="dashboard-sidebar__logo">
        <img 
          v-if="logoUrl"
          :src="logoUrl" 
          alt="Noovox" 
          class="dashboard-sidebar__logo-image"
        />
        <div v-else class="dashboard-sidebar__logo-text">
          <span class="dashboard-sidebar__logo-text-primary">Noovox</span>
          <span class="dashboard-sidebar__logo-text-accent">X</span>
        </div>
      </div>
      </button>
    </div>

    <!-- User Profile Section -->
    <div class="dashboard-sidebar__profile">
      <NvxAvatar 
        :src="userAvatar" 
        :name="userProfile.name"
        :alt="userProfile.name"
        size="xl"
      />
      <div v-if="!collapsed" class="dashboard-sidebar__profile-info">
        <!-- For Self-Serve Profiles: Show Upgrade/Pro badge instead of role dropdown -->
        <template v-if="isSelfServe">
          <button 
            v-if="planType === 'free'" 
            class="dashboard-sidebar__upgrade-btn"
            @click="handleUpgrade"
          >
            Upgrade
          </button>
          <span 
            v-else-if="planType === 'pro'" 
            class="dashboard-sidebar__pro-badge"
          >
            Pro
          </span>
        </template>
        <!-- For Non-Self-Serve: Show role dropdown -->
        <NvxRoleDropdown
          v-else
          v-model="currentRole"
          :can-switch="canSwitchRoles"
          :available-roles="availableRoles"
          @change="handleRoleChange"
        />
      </div>
    </div>

    <!-- Logo Placeholder (hidden for self-serve profiles) -->
    <div v-if="showLogoPlaceholder" v-memo="[logoPlaceholderSrc]" class="dashboard-sidebar__logo-placeholder">
      <img 
        :src="logoPlaceholderSrc" 
        :alt="props.organizationLogoUrl ? 'Company Logo' : 'Logo Placeholder'" 
        class="dashboard-sidebar__logo-placeholder-image"
      />
    </div>

    <!-- Main Navigation -->
    <nav class="dashboard-sidebar__nav">
      <ul class="dashboard-sidebar__nav-list">
        <li v-for="item in navigationItems" :key="item.id" class="dashboard-sidebar__nav-item">
          <NvxSidebarNavItem
            :item="item"
            :active="activeItem === item.id"
            :collapsed="collapsed"
            :role="currentRole"
            @click="handleNavClick(item)"
          />
        </li>
      </ul>
    </nav>

    <!-- Logout Button -->
    <div class="dashboard-sidebar__logout">
      <button 
        class="dashboard-sidebar__logout-button"
        @click="handleLogout"
        :aria-label="'Logout'"
      >
        <NvxIcon name="logout" size="lg" class="dashboard-sidebar__logout-icon" />
      </button>
    </div>

    <!-- Toggle Button -->
    <!-- <button 
      class="dashboard-sidebar__toggle"
      @click="$emit('toggle')"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
    >
      <NvxIcon :name="collapsed ? 'chevron-right' : 'chevron-left'" />
    </button> -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import NvxAvatar from '../UI/NvxAvatar.vue'
import NvxIcon from '../UI/NvxIcon.vue'
import NvxRoleDropdown from '../UI/NvxRoleDropdown.vue'
import NvxSidebarNavItem from './NvxSidebarNavItem.vue'
import logoPlaceholderUrl from '../../assets/logo-placeholder.svg?url'

interface UserProfile {
  name: string
  role: string
  avatar?: string
}

interface TeamMember {
  id: string
  name: string
  role: string
  avatar?: string
}

interface NavigationItem {
  id: string
  label: string
  icon: string
  href?: string
  badge?: string | number
}

interface RoleOption {
  value: string
  label: string
}

interface Props {
  collapsed?: boolean
  userProfile?: UserProfile
  teamMembers?: TeamMember[]
  navigationItems?: NavigationItem[]
  activeItem?: string
  logoUrl?: string
  organizationLogoUrl?: string | null
  canSwitchRoles?: boolean
  availableRoles?: RoleOption[]
  showLogoPlaceholder?: boolean
  isSelfServe?: boolean
  planType?: 'free' | 'pro' | null
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  userProfile: () => ({
    name: 'Admin',
    role: 'Admin',
    avatar: '/assets/example-avatar.png'
  }),
  teamMembers: () => [],
  navigationItems: () => [
    { id: 'team', label: 'Team', icon: 'users' },
    { id: 'analytics', label: 'Analytics', icon: 'bar-chart' },
    { id: 'scheduler', label: 'Scheduler', icon: 'calendar-days' },
    { id: 'content', label: 'Content', icon: 'file-text' },
    { id: 'settings', label: 'Settings', icon: 'settings' },
    { id: 'activity-log', label: 'Activity Log', icon: 'presentation-chart-line' }
  ],
  activeItem: 'team',
  canSwitchRoles: true,
  showLogoPlaceholder: true,
  isSelfServe: false,
  planType: null
})

const emit = defineEmits<{
  toggle: []
  navigate: [item: NavigationItem]
  roleChange: [role: string]
  logout: []
  logoClick: []
  upgrade: []
}>()

const handleNavClick = (item: NavigationItem) => {
  emit('navigate', item)
}

// Local reactive role state
const currentRole = ref(props.userProfile.role)

// Watch for prop changes
watch(() => props.userProfile.role, (newRole) => {
  currentRole.value = newRole
})

const handleRoleChange = (role: string) => {
  currentRole.value = role
  emit('roleChange', role)
  // If authStore is available (in web app context), update it
  // Note: This requires importing useAuthStore, but we can't do that in design-system
  // So we rely on the parent component to handle the roleChange event
}

const handleLogout = () => {
  emit('logout')
}

const handleLogoClick = () => {
  emit('logoClick')
}

const handleUpgrade = () => {
  emit('upgrade')
}

const userAvatar = computed(() => {
  return props.userProfile.avatar
})

// Logo placeholder source - use organization logo if available, otherwise use placeholder
const logoPlaceholderSrc = computed(() => {
  return props.organizationLogoUrl || logoPlaceholderUrl
})
</script>

<style lang="scss" scoped>
.dashboard-sidebar {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: var(--nvx-spacing-4);
  position: relative;
  align-items: center;

  &__header {
    margin-bottom: var(--nvx-spacing-5);
  }

  &__logo-button {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.6;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;

    &-image {
      height: 40px;
      width: auto;
      object-fit: contain;
    }

    &-text {
      display: flex;
      align-items: center;
      gap: var(--nvx-spacing-2);

      &-primary {
        font-size: var(--nvx-font-size-xl);
        font-weight: var(--nvx-font-weight-bold);
        color: var(--nvx-color-text-primary);
      }

      &-accent {
        font-size: var(--nvx-font-size-xl);
        font-weight: var(--nvx-font-weight-bold);
        color: var(--nvx-color-accent-primary);
      }
    }
  }

  &__profile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--nvx-spacing-3);
    margin-bottom: var(--nvx-spacing-8);
    padding: var(--nvx-spacing-3);
    border-radius: var(--nvx-border-radius-lg);
    width: 100%;

    &-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--nvx-spacing-2);
      text-align: center;
      width: 100%;
    }

    &-name {
      display: flex;
      align-items: center;
      gap: var(--nvx-spacing-1);
      font-size: var(--nvx-font-size-xs);
      color: white;
      font-weight: var(--nvx-font-weight-bold);
    }
  }

  &__upgrade-btn {
    display: flex;
    padding: 3px 12px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    color: white;
    border-radius: 14px;
    border: 1px solid var(--2-color-G, #7E40FF);
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%), linear-gradient(239deg, #0023FF 5.09%, #662CDD 80.11%);

    &:hover {
      background: rgba(126, 64, 255, 0.1);
      border-color: #9966FF;
      color: white;
      cursor: pointer;
    }
  }

  &__pro-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 20px;
    background: linear-gradient(135deg, #7E40FF 0%, #2761E9 100%);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: white;
  }

  &__refresh-icon {
    color: white;
    cursor: pointer;
  }

  &__logo-placeholder {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: var(--nvx-spacing-6);
    border: 1px solid #20375B;
    border-radius: var(--nvx-border-radius-full);
    width: 48px;
    height: 48px;
    padding: var(--nvx-spacing-1);

    &-image {
      width: 33px;
      height: 31px;
      object-fit: contain;
      transition: all 0.2s ease;
      opacity: 0.8;

      &:hover {
        opacity: 1;
        transform: scale(1.05);
      }
    }
  }

  &__nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--nvx-spacing-6);
    width: 100%;

    &-list {
      list-style: none;
      gap: 32px;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
    }

    &-item {
      width: 100%;
    }
  }

  &__team {
    margin-bottom: var(--nvx-spacing-6);

    &-title {
      font-size: var(--nvx-font-size-sm);
      font-weight: var(--nvx-font-weight-semibold);
      color: var(--nvx-color-text-secondary);
      margin-bottom: var(--nvx-spacing-4);
      text-transform: uppercase;
      letter-spacing: var(--nvx-letter-spacing-wide);
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: var(--nvx-spacing-3);
    }

    &-member {
      display: flex;
      align-items: center;
      gap: var(--nvx-spacing-3);
      padding: var(--nvx-spacing-2);
      border-radius: var(--nvx-border-radius-md);
      transition: background-color 0.2s ease;

      &:hover {
        background-color: var(--nvx-color-background-secondary);
      }

      &-info {
        flex: 1;
        min-width: 0;
      }

      &-name {
        font-size: var(--nvx-font-size-sm);
        font-weight: var(--nvx-font-weight-medium);
        color: var(--nvx-color-text-primary);
      }

      &-role {
        font-size: var(--nvx-font-size-xs);
        color: var(--nvx-color-text-secondary);
      }
    }
  }

  &__logout {
    margin-top: auto;
    padding-top: var(--nvx-spacing-4);
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__logout-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background-color: transparent;
    border: none;
    border-radius: var(--nvx-border-radius-md);
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--nvx-color-text-primary);

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  &__logout-icon {
    color: var(--nvx-color-text-primary);
  }

  &__toggle {
    position: absolute;
    top: var(--nvx-spacing-6);
    right: -12px;
    width: 24px;
    height: 24px;
    background-color: var(--nvx-color-background-secondary);
    border: 1px solid var(--nvx-color-border-primary);
    border-radius: var(--nvx-border-radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: var(--nvx-z-index-base);

    &:hover {
      background-color: var(--nvx-color-background-elevated);
      border-color: var(--nvx-color-border-accent);
    }
  }

  &__logo-chevron {
    position: absolute;
    top: 30px;
    right: -8px;
    background-color: #20375B;
    border-radius: var(--nvx-border-radius-full);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10;
    color: white;
    stroke: white;
    fill: none;

    &:hover {
      transform: rotate(180deg);
      background-color: #25416A;
    }

    :deep(svg) {
      color: white;
      stroke: white;
      fill: none;
      width: 16px;
      height: 16px;
    }
  }
}
</style>
